// src/services/warehouse-transfer.service.ts
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

// ── Types ──────────────────────────────────────────────────────────────────

export type WarehouseTransferStatus = "Completed" | "Cancelled";

export interface WarehouseTransferItem {
  id: string;
  transferId: string;
  sku: string;
  productName: string;
  quantityRequested: number;
  quantityTransferred: number;
}

export interface WarehouseTransfer {
  id: string;
  organizationId: string;
  refNumber: string;
  fromWarehouseId: string;
  fromWarehouseName: string;
  toWarehouseId: string;
  toWarehouseName: string;
  status: WarehouseTransferStatus;
  notes: string | null;
  requestedBy: string | null;
  createdAt: string;
  items: WarehouseTransferItem[];
}

export interface WarehouseTransferCreateInput {
  fromWarehouseId: string;
  toWarehouseId: string;
  notes?: string;
  items: Array<{
    sku: string;
    productName: string;
    quantityRequested: number;
  }>;
}

export interface WarehouseStockByLocation {
  warehouseId: string;
  warehouseName: string;
  sku: string;
  productName: string;
  receivedQty: number;
  transferredOut: number;
  transferredIn: number;
  availableQuantity: number;
}

export interface WarehouseStockSummary {
  warehouseId: string;
  warehouseName: string;
  warehouseCode: string | null;
  isActive: boolean;
  skuCount: number;
  totalAvailableUnits: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────

async function generateRefNumber(orgId: string): Promise<string> {
  const { data } = await supabase
    .from("warehouse_transfers")
    .select("ref_number")
    .eq("organization_id", orgId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  let nextNum = 1;
  if (data?.ref_number) {
    const match = /(\d+)$/.exec(data.ref_number);
    if (match) nextNum = parseInt(match[1], 10) + 1;
  }
  return `TRF-${String(nextNum).padStart(3, "0")}`;
}

function mapItem(row: Record<string, unknown>): WarehouseTransferItem {
  return {
    id: String(row.id ?? ""),
    transferId: String(row.transfer_id ?? ""),
    sku: String(row.sku ?? ""),
    productName: String(row.product_name ?? ""),
    quantityRequested: Number(row.quantity_requested ?? 0),
    quantityTransferred: Number(row.quantity_transferred ?? row.quantity_requested ?? 0),
  };
}

function mapTransfer(row: Record<string, unknown>): WarehouseTransfer {
  const rawItems = Array.isArray(row.warehouse_transfer_items)
    ? (row.warehouse_transfer_items as Record<string, unknown>[])
    : [];
  return {
    id: String(row.id ?? ""),
    organizationId: String(row.organization_id ?? ""),
    refNumber: String(row.ref_number ?? ""),
    fromWarehouseId: String(row.from_warehouse_id ?? ""),
    fromWarehouseName: String((row.from_warehouse as Record<string, unknown>)?.name ?? ""),
    toWarehouseId: String(row.to_warehouse_id ?? ""),
    toWarehouseName: String((row.to_warehouse as Record<string, unknown>)?.name ?? ""),
    status: (row.status as WarehouseTransferStatus) ?? "Completed",
    notes: (row.notes as string) ?? null,
    requestedBy: (row.requested_by as string) ?? null,
    createdAt: String(row.created_at ?? ""),
    items: rawItems.map((i) => mapItem(i)),
  };
}

// ── Service ────────────────────────────────────────────────────────────────

export const WarehouseTransferService = {
  /**
   * Create and immediately complete a warehouse transfer.
   * Stock adjusts instantly via v_warehouse_stock_by_location.
   */
  async create(input: WarehouseTransferCreateInput): Promise<WarehouseTransfer | null> {
    const orgId = await getCurrentOrganizationId();
    if (!orgId) return null;

    const { data: { user } } = await supabase.auth.getUser();
    const refNumber = await generateRefNumber(orgId);

    // Insert transfer as Completed immediately — no approval step
    const { data: transfer, error: tErr } = await supabase
      .from("warehouse_transfers")
      .insert({
        organization_id: orgId,
        ref_number: refNumber,
        from_warehouse_id: input.fromWarehouseId,
        to_warehouse_id: input.toWarehouseId,
        status: "Completed",
        notes: input.notes ?? null,
        requested_by: user?.id ?? null,
      })
      .select()
      .single();

    if (tErr || !transfer) {
      console.error("[WarehouseTransferService] create error:", tErr?.message);
      return null;
    }

    // Insert items with quantity_transferred = quantity_requested immediately
    const { error: itemErr } = await supabase
      .from("warehouse_transfer_items")
      .insert(
        input.items.map((item) => ({
          transfer_id: transfer.id,
          sku: item.sku,
          product_name: item.productName,
          quantity_requested: item.quantityRequested,
          quantity_transferred: item.quantityRequested,
        }))
      );

    if (itemErr) {
      console.error("[WarehouseTransferService] items insert error:", itemErr.message);
      return null;
    }

    return this.getById(transfer.id);
  },

  async list(
    page: number,
    pageSize: number,
  ): Promise<{ transfers: WarehouseTransfer[]; count: number }> {
    const orgId = await getCurrentOrganizationId();
    if (!orgId) return { transfers: [], count: 0 };

    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from("warehouse_transfers")
      .select(
        "*, from_warehouse:from_warehouse_id(name), to_warehouse:to_warehouse_id(name), warehouse_transfer_items(*)",
        { count: "exact" }
      )
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[WarehouseTransferService] list error:", error.message);
      return { transfers: [], count: 0 };
    }

    return {
      transfers: (data ?? []).map((r) => mapTransfer(r as Record<string, unknown>)),
      count: count ?? 0,
    };
  },

  async getById(id: string): Promise<WarehouseTransfer | null> {
    const { data, error } = await supabase
      .from("warehouse_transfers")
      .select("*, from_warehouse:from_warehouse_id(name), to_warehouse:to_warehouse_id(name), warehouse_transfer_items(*)")
      .eq("id", id)
      .single();

    if (error || !data) return null;
    return mapTransfer(data as Record<string, unknown>);
  },

  /**
   * Returns available stock for a specific warehouse.
   * Used by TransferRequestModal to populate the SKU picker.
   */
  async listStockByWarehouse(warehouseId: string): Promise<WarehouseStockByLocation[]> {
    const { data, error } = await supabase
      .from("v_warehouse_stock_by_location")
      .select("*")
      .eq("warehouse_id", warehouseId)
      .gt("available_quantity", 0)
      .order("product_name", { ascending: true });

    if (error) {
      console.error("[WarehouseTransferService] listStockByWarehouse error:", error.message);
      return [];
    }

    return (data ?? []).map((r) => ({
      warehouseId: String(r.warehouse_id ?? ""),
      warehouseName: String(r.warehouse_name ?? ""),
      sku: String(r.sku ?? ""),
      productName: String(r.product_name ?? ""),
      receivedQty: Number(r.received_qty ?? 0),
      transferredOut: Number(r.transferred_out ?? 0),
      transferredIn: Number(r.transferred_in ?? 0),
      availableQuantity: Number(r.available_quantity ?? 0),
    }));
  },

  /**
   * Returns per-warehouse stock summary for the Warehouses tab.
   * All active warehouses are included even if they have zero stock.
   */
  async listSummaries(): Promise<WarehouseStockSummary[]> {
    const orgId = await getCurrentOrganizationId();
    if (!orgId) return [];

    const { data, error } = await supabase
      .from("v_warehouse_stock_summary")
      .select("*")
      .eq("organization_id", orgId)
      .order("warehouse_name", { ascending: true });

    if (error) {
      console.error("[WarehouseTransferService] listSummaries error:", error.message);
      return [];
    }

    return (data ?? []).map((r) => ({
      warehouseId: String(r.warehouse_id ?? ""),
      warehouseName: String(r.warehouse_name ?? ""),
      warehouseCode: (r.warehouse_code as string) ?? null,
      isActive: Boolean(r.is_active ?? true),
      skuCount: Number(r.sku_count ?? 0),
      totalAvailableUnits: Number(r.total_available_units ?? 0),
    }));
  },
};
